import { type TranscriptEntry } from '@apps/web/components/phoebe_chat/transcript_types';
import {
    getQuestionChoiceRows,
    getUserQuestions,
} from '@apps/web/components/phoebe_chat/user_question_model';
import { parseV3ChatAttachment } from '@apps/web/features/phoebe_chat_v3/model/attachment';
import { parseV3CommsPresentation } from '@apps/web/features/phoebe_chat_v3/model/comms_presentation';
import { parseV3DataPresentation } from '@apps/web/features/phoebe_chat_v3/model/data_presentation';
import { parseV3OutreachLink } from '@apps/web/features/phoebe_chat_v3/model/outreach_link';
import { parseV3OutreachPresentation } from '@apps/web/features/phoebe_chat_v3/model/outreach_presentation';
import {
    getUserQuestionPresentation,
    type UserQuestionPresentation,
} from '@apps/web/features/phoebe_chat_v3/model/question_presentation';
import {
    type V3Question,
    type V3ToolEntry,
} from '@apps/web/features/phoebe_chat_v3/model/transcript';

type SourceToolEntry = Extract<
    TranscriptEntry,
    { readonly kind: 'tool-execution' }
>;

function valueRevision(value: unknown): string {
    if (value === undefined) {
        return 'undefined';
    }
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
}

function buildRevision(parts: ReadonlyArray<unknown>): string {
    return parts.map(valueRevision).join('');
}

function adaptQuestionState(entry: SourceToolEntry): {
    readonly questions: ReadonlyArray<V3Question>;
    readonly presentation: UserQuestionPresentation | null;
} {
    if (entry.status !== 'pending_user_input' || entry.isComplete) {
        return { questions: [], presentation: null };
    }
    const sourceQuestions = getUserQuestions(entry);
    const questions = sourceQuestions.map((question) => ({
        id: question.id,
        prompt: question.displayQuestion,
        header: question.header,
        inputType: question.inputType,
        choices: getQuestionChoiceRows(question),
        placeholder: question.placeholder,
        allowOther: question.allowOther,
    }));
    return {
        questions,
        presentation: getUserQuestionPresentation(entry, sourceQuestions),
    };
}

export function adaptInteractiveTool({
    entry,
    activityLabel,
    showOutreachPresentations,
}: {
    readonly entry: SourceToolEntry;
    readonly activityLabel: string | null;
    readonly showOutreachPresentations: boolean;
}): V3ToolEntry {
    const questionState = adaptQuestionState(entry);
    const parseArgs = {
        toolName: entry.name,
        runId: entry.runId,
        eventId: entry.id,
        output: entry.output,
    };
    const dataPresentation = parseV3DataPresentation(parseArgs);
    const outreachPresentation = showOutreachPresentations
        ? parseV3OutreachPresentation({
              toolName: entry.name,
              output: entry.output,
          })
        : null;
    const chatAttachment = parseV3ChatAttachment(parseArgs);
    const status = entry.status ?? (entry.isComplete ? 'complete' : 'running');
    const commsPresentation = parseV3CommsPresentation(entry, status);
    const outreachLink = parseV3OutreachLink(parseArgs);
    return {
        kind: 'tool',
        id: entry.id,
        createdAt: entry.createdAt,
        name: entry.name,
        runId: entry.runId ?? null,
        agentTurnId: entry.agentTurnId ?? null,
        toolCallEventId: entry.toolCallEventId ?? null,
        callId: entry.callId,
        status,
        isComplete: entry.isComplete,
        approvalState: entry.approvalState ?? null,
        arguments: entry.arguments,
        output: entry.output,
        error: entry.error ? 'Phoebe could not complete this step.' : null,
        questions: questionState.questions,
        questionPresentation: questionState.presentation,
        dataPresentation,
        outreachPresentation,
        chatAttachment,
        commsPresentation,
        outreachLink,
        activityLabel,
        revision: buildRevision([
            entry.name,
            entry.runId,
            entry.agentTurnId,
            entry.toolCallEventId,
            entry.callId,
            status,
            entry.isComplete,
            entry.approvalState,
            entry.arguments,
            entry.output,
            Boolean(entry.error),
            questionState.questions,
            questionState.presentation,
            dataPresentation,
            outreachPresentation,
            chatAttachment,
            commsPresentation,
            outreachLink,
            activityLabel,
        ]),
    };
}

export function isInteractiveTool({
    entry,
    normalized,
}: {
    readonly entry: SourceToolEntry;
    readonly normalized: V3ToolEntry;
}): boolean {
    const needsApproval =
        !entry.isComplete &&
        (entry.approvalState === 'pending' ||
            entry.status === 'pending_approval');
    return (
        needsApproval ||
        normalized.questions.length > 0 ||
        normalized.questionPresentation !== null ||
        normalized.dataPresentation !== null ||
        normalized.outreachPresentation !== null ||
        normalized.chatAttachment !== null ||
        normalized.commsPresentation !== null ||
        normalized.outreachLink !== null
    );
}
